import { useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import type { CreateAdminUserResponseOutput } from '@schemas'

import { useCreateAdminUser } from '@/api/hooks'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { parseApiErrorMessage } from '@/lib/api-errors'
import { AccessLinkPanel, PersonPicker, type PickedPerson } from './account-components'

const inviteSchema = z.object({
  email: z.string().email('Enter a valid email address'),
  isAdmin: z.boolean().optional(),
})

type InviteFormValues = z.infer<typeof inviteSchema>

/**
 * Invites an account and shows its setup link once. `person`, when given, starts the account
 * linked to that person, as when inviting a staff member from Setup → Staff.
 */
export function InviteUserDialog({
  person: initialPerson = null,
  onClose,
}: {
  person?: PickedPerson | null
  onClose: () => void
}) {
  const createUser = useCreateAdminUser()
  const [person, setPerson] = useState<PickedPerson | null>(initialPerson)
  const [invited, setInvited] = useState<CreateAdminUserResponseOutput | null>(null)
  const form = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: '', isAdmin: false },
  })

  const invite = async (values: InviteFormValues) => {
    try {
      const result = await createUser.mutateAsync({
        email: values.email.trim(),
        ...(values.isAdmin ? { isAdmin: true } : {}),
        ...(person ? { personId: person.id } : {}),
      })
      // The dialog itself now shows the invitation and its link.
      setInvited(result)
    } catch (error) {
      toast.error(parseApiErrorMessage(error))
    }
  }

  return (
    <Dialog open onOpenChange={open => !open && !createUser.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite user</DialogTitle>
          <DialogDescription>
            {invited
              ? 'Give this link to the person. They choose their own password with it.'
              : 'The person receives a one-time setup link and chooses their own password.'}
          </DialogDescription>
        </DialogHeader>
        {invited ? (
          <>
            <AccessLinkPanel
              link={invited.accessLink}
              userId={invited.user.id}
              email={invited.user.email}
            />
            <DialogFooter>
              <Button type="button" onClick={onClose}>
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(invite)}
              className="space-y-4"
              data-testid="invite-user-form"
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" autoComplete="off" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isAdmin"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-md border border-input px-3 py-2">
                    <FormLabel className="mb-0 font-medium">Grant admin access</FormLabel>
                    <FormControl>
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={field.value ?? false}
                        onChange={event => field.onChange(event.target.checked)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                        aria-label="Grant admin access"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <details className="rounded-md border px-3 py-2" open={Boolean(initialPerson)}>
                <summary className="cursor-pointer text-sm font-medium">
                  Link to a person (optional)
                </summary>
                <p className="mt-2 text-xs text-muted-foreground">
                  The account gets this person's staff roles in every show where they are staff.
                  Type the email yourself: a person's contact email can be edited by show staff.
                </p>
                <div className="mt-2">
                  <PersonPicker selected={person} onSelect={setPerson} />
                </div>
              </details>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onClose}
                  disabled={createUser.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createUser.isPending}>
                  {createUser.isPending ? 'Inviting…' : 'Invite user'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
